import { useRef, useState } from "react";
import {
  useListAccountDocuments,
  useListAccountContacts,
  useUpdateAccountContact,
  getListAccountDocumentsQueryKey,
  getListAccountContactsQueryKey,
  type Document as AccountDocument,
  type Contact,
  CreateContactBodyIdProofType,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { uploadDocument } from "@/lib/upload";
import {
  Upload,
  FileText,
  ExternalLink,
  CheckCircle2,
  Circle,
  Building2,
} from "lucide-react";
import { format } from "date-fns";

// Phase 23-07 + 2026-06-17: account documents — restructured into a per-member
// KYC accordion + a company-verification section (driven by the account type),
// plus the signed-agreement row, an ad-hoc uploader, and the full history list.
type DocKind =
  | "agreement"
  | "kyc"
  | "deposit_receipt"
  | "handover"
  | "exit_settlement"
  | "company_registration"
  | "gst_certificate"
  | "gst_return";

const DOC_LABELS: Record<DocKind, string> = {
  agreement: "Agreement",
  kyc: "KYC",
  deposit_receipt: "Deposit Receipt",
  handover: "Handover",
  exit_settlement: "Exit Settlement",
  company_registration: "Company Registration",
  gst_certificate: "GST Certificate",
  gst_return: "GST Return",
};

// Ad-hoc types the operator can still upload beyond the structured sections.
const OTHER_DOC_OPTIONS: DocKind[] = ["deposit_receipt", "handover", "exit_settlement", "gst_return"];

// ID-proof types — mirrors the Members tab contact form (PAN / DL / Passport / Aadhaar).
const ID_PROOF_TYPES = Object.values(CreateContactBodyIdProofType);
type IdProofType = (typeof ID_PROOF_TYPES)[number];

interface Props {
  accountId: number;
  accountType?: string; // 'individual' | 'company'
  gstTreatment?: string; // 'registered_b2b' | 'unregistered_b2c'
}

// What the shared hidden file input should do once a file is chosen.
interface PendingUpload {
  type: DocKind;
  key: string; // identifies which slot/button is busy (one upload at a time)
  contactId?: number;
  signedAt?: string;
  // For member KYC: the ID type/number to persist onto the contact before upload.
  idType?: string;
  idNumber?: string;
}

export function DocumentsList({ accountId, accountType = "individual", gstTreatment }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateContact = useUpdateAccountContact();

  const { data: documents, isLoading } = useListAccountDocuments(accountId);
  const { data: contacts } = useListAccountContacts(accountId);

  // All members, primary pinned to the top, then by id (exited members kept so
  // their KYC stays on file).
  const members = [...((contacts as Contact[] | undefined) ?? [])].sort((a, b) => {
    const ap = a.roles?.includes("primary") ? 0 : 1;
    const bp = b.roles?.includes("primary") ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return a.id - b.id;
  });

  // Company-verification checklist (company accounts only). GST certificate is
  // only relevant for GST-registered (B2B) companies — matches the prior rule.
  const companyDocs: DocKind[] =
    accountType === "company"
      ? gstTreatment === "registered_b2b"
        ? ["company_registration", "gst_certificate"]
        : ["company_registration"]
      : [];

  const [pending, setPending] = useState<PendingUpload | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [otherType, setOtherType] = useState<DocKind>("deposit_receipt");
  const [signedAt, setSignedAt] = useState("");

  // Per-member KYC inputs (ID type + number) before upload, keyed by contact id.
  const [kycInputs, setKycInputs] = useState<Record<number, { idType: string; idNumber: string }>>({});
  function kycInputFor(member: Contact): { idType: string; idNumber: string } {
    return (
      kycInputs[member.id] ?? {
        idType: member.idProofType ?? "",
        idNumber: member.idProofRef ?? "",
      }
    );
  }
  function setKycInput(member: Contact, patch: Partial<{ idType: string; idNumber: string }>) {
    setKycInputs((prev) => ({ ...prev, [member.id]: { ...kycInputFor(member), ...patch } }));
  }

  function docOfType(type: DocKind): AccountDocument | undefined {
    return (documents as AccountDocument[] | undefined)?.find((d) => d.type === type);
  }
  function kycDocFor(contactId: number): AccountDocument | undefined {
    return (documents as AccountDocument[] | undefined)?.find(
      (d) => d.type === "kyc" && d.contactId === contactId,
    );
  }

  function pickFileFor(p: PendingUpload) {
    setPending(p);
    fileInputRef.current?.click();
  }

  function pickKycFor(member: Contact) {
    const input = kycInputFor(member);
    pickFileFor({
      type: "kyc",
      key: `kyc:${member.id}`,
      contactId: member.id,
      idType: input.idType,
      idNumber: input.idNumber,
    });
  }

  async function onFileChosen(file: File | undefined) {
    if (!file || !pending) {
      setPending(null);
      return;
    }
    const p = pending;
    setBusyKey(p.key);
    try {
      // For member KYC, persist the entered ID type/number onto the contact
      // first (so it shows on the Members tab too), then upload the document.
      if (p.type === "kyc" && p.contactId != null) {
        const member = members.find((m) => m.id === p.contactId);
        const typeChanged = (p.idType ?? "") !== (member?.idProofType ?? "");
        const numberChanged = (p.idNumber ?? "") !== (member?.idProofRef ?? "");
        if (typeChanged || numberChanged) {
          await updateContact.mutateAsync({
            id: accountId,
            contactId: p.contactId,
            data: {
              idProofType: (p.idType ? (p.idType as IdProofType) : null) as IdProofType | null,
              idProofRef: p.idNumber || null,
            },
          });
          queryClient.invalidateQueries({ queryKey: getListAccountContactsQueryKey(accountId) });
        }
      }

      await uploadDocument(accountId, p.type, file, {
        contactId: p.contactId,
        signedAt: p.signedAt || undefined,
      });
      toast({ title: `${DOC_LABELS[p.type]} uploaded.` });
      queryClient.invalidateQueries({ queryKey: getListAccountDocumentsQueryKey(accountId) });
      setSignedAt("");
    } catch (err) {
      toast({ title: `Upload failed: ${(err as Error)?.message ?? "error"}`, variant: "destructive" });
    } finally {
      setBusyKey(null);
      setPending(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function isImage(url: string): boolean {
    return /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
  }

  // A single required-document row (used for company verification + agreement).
  function RequiredDocRow({ type }: { type: DocKind }) {
    const existing = docOfType(type);
    const key = `doc:${type}`;
    const busy = busyKey === key;
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {existing ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
          ) : (
            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium">{DOC_LABELS[type]}</p>
            <p className="text-[11px] text-muted-foreground">
              {existing ? `Uploaded ${format(new Date(existing.uploadedAt), "dd MMM yyyy")}` : "Not uploaded yet"}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {existing && <Badge variant="outline" className="text-[10px] text-green-700">Uploaded</Badge>}
          {existing?.signedUrl && (
            <a href={existing.signedUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm">
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> View
              </Button>
            </a>
          )}
          <Button
            variant={existing ? "outline" : "default"}
            size="sm"
            disabled={busy}
            onClick={() => pickFileFor({ type, key })}
          >
            {busy ? "Uploading…" : existing ? "Replace" : (
              <>
                <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2">
      {/* Shared hidden file input — its target is set by the clicked slot. */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf"
        onChange={(e) => onFileChosen(e.target.files?.[0] ?? undefined)}
      />

      {/* ── KYC — Members (per-member accordion) ───────────────────────────── */}
      <div className="rounded-md border p-4 space-y-3">
        <div>
          <h3 className="text-sm font-medium">KYC — Members</h3>
          <p className="text-xs text-muted-foreground">
            Identity proof for each member on this account. The primary member appears first.
          </p>
        </div>

        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No members yet — add members from the Members tab first.
          </p>
        ) : (
          <Accordion type="multiple" className="w-full">
            {members.map((member) => {
              const existing = kycDocFor(member.id);
              const isPrimary = member.roles?.includes("primary");
              const input = kycInputFor(member);
              const isAadhaar = input.idType === "Aadhaar";
              const busy = busyKey === `kyc:${member.id}`;
              return (
                <AccordionItem key={member.id} value={`m-${member.id}`}>
                  <AccordionTrigger className="py-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
                      {existing ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                      ) : (
                        <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate font-medium">{member.fullName}</span>
                      {isPrimary && (
                        <Badge variant="secondary" className="text-[10px]">Primary</Badge>
                      )}
                      {member.status === "exited" && (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground">Exited</Badge>
                      )}
                      <span className="ml-auto text-[11px] font-normal text-muted-foreground">
                        {existing ? "KYC on file" : "KYC pending"}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {existing ? (
                      <div className="space-y-2 px-1">
                        <p className="text-sm">
                          <span className="text-muted-foreground">ID:</span>{" "}
                          {member.idProofType ?? "—"}
                          {member.idProofRef ? ` • ${member.idProofRef}` : ""}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="text-[10px] text-green-700">
                            Uploaded {format(new Date(existing.uploadedAt), "dd MMM yyyy")}
                          </Badge>
                          {existing.signedUrl && (
                            <a href={existing.signedUrl} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> View
                              </Button>
                            </a>
                          )}
                          <Button variant="outline" size="sm" disabled={busy} onClick={() => pickKycFor(member)}>
                            {busy ? "Uploading…" : "Replace"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3 px-1">
                        <p className="text-xs text-muted-foreground">
                          {isPrimary
                            ? "ID captured at account creation — confirm and upload the document."
                            : "Select the ID type, enter the number, and upload the document."}
                        </p>
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="text-sm font-medium leading-none">ID Type</label>
                            <Select
                              value={input.idType || undefined}
                              onValueChange={(v) => setKycInput(member, { idType: v })}
                            >
                              <SelectTrigger className="mt-1.5 w-[160px]">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                {ID_PROOF_TYPES.map((t) => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm font-medium leading-none">
                              ID Number
                              {isAadhaar && (
                                <span className="ml-1 text-xs text-muted-foreground">(last 4 only)</span>
                              )}
                            </label>
                            <Input
                              className="mt-1.5 w-[200px]"
                              value={input.idNumber}
                              placeholder={isAadhaar ? "Last 4 digits only" : "ID number"}
                              onChange={(e) => setKycInput(member, { idNumber: e.target.value })}
                            />
                          </div>
                          <Button size="sm" disabled={busy} onClick={() => pickKycFor(member)}>
                            {busy ? "Uploading…" : (
                              <>
                                <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload
                              </>
                            )}
                          </Button>
                        </div>
                        {isAadhaar && (
                          <p className="text-xs text-amber-600">
                            Aadhaar: store the last 4 digits only.
                          </p>
                        )}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      {/* ── Company verification (company accounts only) ────────────────────── */}
      {companyDocs.length > 0 && (
        <div className="rounded-md border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <div>
              <h3 className="text-sm font-medium">Company verification</h3>
              <p className="text-xs text-muted-foreground">
                Company registration{gstTreatment === "registered_b2b" ? " and GST documents" : ""}.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {companyDocs.map((type) => (
              <RequiredDocRow key={type} type={type} />
            ))}
          </div>
        </div>
      )}

      {/* ── Signed agreement ───────────────────────────────────────────────── */}
      <div className="rounded-md border p-4 space-y-3">
        <div>
          <h3 className="text-sm font-medium">Agreement</h3>
          <p className="text-xs text-muted-foreground">The signed membership agreement.</p>
        </div>
        <RequiredDocRow type="agreement" />
      </div>

      {/* ── Ad-hoc upload for other document types ──────────────────────────── */}
      <div className="rounded-md border p-4 space-y-3">
        <h3 className="text-sm font-medium">Upload another document</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-sm font-medium leading-none">Type</label>
            <Select value={otherType} onValueChange={(v) => setOtherType(v as DocKind)}>
              <SelectTrigger className="mt-1.5 w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OTHER_DOC_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>{DOC_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium leading-none">Signed Date (optional)</label>
            <Input
              type="date"
              className="mt-1.5 w-[180px]"
              value={signedAt}
              onChange={(e) => setSignedAt(e.target.value)}
            />
          </div>
          <Button
            type="button"
            size="sm"
            disabled={busyKey === "other"}
            onClick={() => pickFileFor({ type: otherType, key: "other", signedAt })}
          >
            {busyKey === "other" ? "Uploading…" : (
              <>
                <Upload className="mr-2 h-4 w-4" /> Choose &amp; Upload
              </>
            )}
          </Button>
        </div>
      </div>

      {/* ── All uploaded documents (full history) ──────────────────────────── */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">All documents</h3>
        {isLoading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-md border">
              <Skeleton className="h-10 w-10 rounded" />
              <div className="space-y-1 flex-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))
        ) : !documents || documents.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No documents uploaded yet.
          </p>
        ) : (
          (documents as AccountDocument[]).map((doc) => {
            // Private bucket → view via the short-lived signed URL, not the path.
            const viewUrl = doc.signedUrl ?? null;
            // Member-tied docs (KYC) carry a contactId — surface the name so the
            // row reads e.g. "KYC — Jeetu Singh". Company/agreement docs have none.
            const memberName =
              doc.contactId != null
                ? members.find((m) => m.id === doc.contactId)?.fullName
                : undefined;
            return (
              <div key={doc.id} className="flex items-center gap-3 p-3 rounded-md border hover:bg-muted/30">
                {isImage(doc.fileUrl) && viewUrl ? (
                  <img src={viewUrl} alt={doc.type} className="h-12 w-12 object-cover rounded" />
                ) : (
                  <div className="h-12 w-12 flex items-center justify-center bg-muted rounded">
                    <FileText className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {DOC_LABELS[doc.type as DocKind] ?? doc.type}
                    </Badge>
                    {memberName && (
                      <span className="truncate text-sm font-medium">{memberName}</span>
                    )}
                    {doc.signedAt && (
                      <span className="text-xs text-muted-foreground">
                        Signed {format(new Date(doc.signedAt), "MMM d, yyyy")}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Uploaded {format(new Date(doc.uploadedAt), "MMM d, yyyy")}
                    {doc.uploadedBy && ` by User #${doc.uploadedBy}`}
                  </p>
                </div>
                <a
                  href={viewUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={viewUrl ? "" : "pointer-events-none opacity-40"}
                >
                  <Button variant="ghost" size="icon">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
